/**
 * CommandrContext.tsx - CommandrContext - React context for keyboard shortcuts
 * 
 * CHANGE HISTORY:
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { createContext, useContext, useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { getCommandr } from './CommandrManager';

const CommandrContext = createContext(null);

// Commandr works standalone - actr integration is optional
export const CommandrProvider = ({ children, actr = null }) => {
  // Actr can be passed as a prop if needed, otherwise null
  const shortcutsRef = useRef(getCommandr({ actr }));
  const [, forceUpdate] = useState({});
  
  const shortcuts = shortcutsRef.current;
  
  // Subscribe to changes
  useEffect(() => {
    const unsubscribe = shortcuts.subscribe(() => {
      forceUpdate({});
    });
    
    return unsubscribe;
  }, [shortcuts]);
  
  // Create callbacks at top level
  const register = useCallback((key, handler, options) => shortcuts.register(key, handler, options), [shortcuts]);
  const unregister = useCallback((key) => shortcuts.unregister(key), [shortcuts]);
  const setScope = useCallback((scope) => shortcuts.setScope(scope), [shortcuts]);
  const getScope = useCallback(() => shortcuts.getScope(), [shortcuts]);
  const setEnabled = useCallback((enabled) => shortcuts.setEnabled(enabled), [shortcuts]);
  const getShortcuts = useCallback((scope) => shortcuts.getShortcuts(scope), [shortcuts]);
  const getScopes = useCallback(() => shortcuts.getScopes(), [shortcuts]);
  const getDisplayString = useCallback((key) => shortcuts.getDisplayString(key), [shortcuts]);
  
  const value = useMemo(() => ({
    shortcuts,
    register,
    unregister,
    setScope,
    getScope,
    setEnabled,
    getShortcuts,
    getScopes,
    getDisplayString
  }), [register, unregister, setScope, getScope, setEnabled, getShortcuts, getScopes, getDisplayString, shortcuts]);
  
  return (
    <CommandrContext.Provider value={value}>
      {children}
    </CommandrContext.Provider>
  );
};

export const useCommandr = () => {
  const context = useContext(CommandrContext);
  
  if (!context) {
    throw new Error('useCommandr must be used within CommandrProvider');
  }
  
  return context;
};

/**
 * Hook to register shortcuts for a component
 * Automatically unregisters on unmount
 */
export const useShortcut = (key, handler, options = {}) => {
  const { register } = useCommandr();
  
  useEffect(() => {
    const unregister = register(key, handler, options);
    return unregister;
  }, [key, handler, options, register]);
};

/**
 * Hook to set scope for a component
 * Automatically resets on unmount
 */
export const useShortcutScope = (scope) => {
  const { setScope } = useCommandr();
  const previousScope = useRef(null);
  
  useEffect(() => {
    previousScope.current = getCommandr().getScope();
    setScope(scope);
    
    return () => {
      if (previousScope.current) {
        setScope(previousScope.current);
      }
    };
  }, [scope, setScope]);
};

export default CommandrContext;
